import { z } from "zod";
import { PAGE_SIZE_MAX, keysetQuery, searchParam } from "./pagination";

/**
 * Accreditation (M3): one card per accreditation, `ac_cards`.
 *
 * What lives here is what BOTH sides of the wire must agree on: the status
 * vocabulary, the transition graph, the list query, the limits and the one
 * definition of how a card number is printed. The database CHECK on
 * `ac_cards.status` is generated from `AC_CARD_STATUSES` (`inList()`), and the
 * status-guard trigger `fn_ac_card_status_guard` (migration 0019) hard-codes
 * the (from, to) pairs of `AC_CARD_TRANSITIONS` — a unit test parses the
 * migration and holds the two to the same set.
 */

// ── Statuses ────────────────────────────────────────────────────────────────

/**
 * The WORKFLOW status of a card, not its physical state. Printing, collection
 * and activation get their own column in the printing milestone, so workflow
 * and card state never share one field.
 *
 *   DRAFT      the organisation is still filling it in
 *   SUBMITTED  sent to the host
 *   PENDING    the host is checking security & eligibility
 *   APPROVED   accepted; the card now has an accreditation number
 *   RETURN     sent back to the organisation to fix (remark in `status_note`)
 *   REJECTED   refused (remark in `status_note`)
 *   WITHDRAWN  taken back by the organisation or the host
 */
export const AC_CARD_STATUSES = ["DRAFT", "SUBMITTED", "PENDING", "APPROVED", "RETURN", "REJECTED", "WITHDRAWN"] as const;
export type AcCardStatus = (typeof AC_CARD_STATUSES)[number];

/** Statuses in which the registering organisation may edit the card. */
export const AC_CARD_EDITABLE_STATUSES = ["DRAFT", "RETURN"] as const satisfies readonly AcCardStatus[];

// ── Transition graph ────────────────────────────────────────────────────────

export const AC_CARD_TRANSITION_ACTIONS = ["submit", "unsubmit", "pending", "approve", "return", "reject", "withdraw", "reopen"] as const;
export type AcCardTransitionAction = (typeof AC_CARD_TRANSITION_ACTIONS)[number];

/**
 * Who may take an edge.
 *
 *   ORG          the registrar side: a scoped ORGANIZATION / OPERATION login
 *                whose group holds the action's permission and whose scope
 *                covers the card, or ADMIN / SUPER_ADMIN acting as registrar.
 *   HOST         ADMIN / SUPER_ADMIN, or an OPERATION group holding
 *                `accreditation.approve` with the card in scope. A NOC-type
 *                (ORGANIZATION) group can never act as host.
 *   SUPER_ADMIN  only SUPER_ADMIN.
 */
export const AC_CARD_TRANSITION_ACTORS = ["ORG", "HOST", "SUPER_ADMIN"] as const;
export type AcCardTransitionActor = (typeof AC_CARD_TRANSITION_ACTORS)[number];

export interface AcCardTransition {
	from: AcCardStatus;
	to: AcCardStatus;
	by: AcCardTransitionActor;
	action: AcCardTransitionAction;
	/** A remark for the organisation is required; it is stored in `status_note`. */
	remarks_required?: boolean;
	/** A reason is required; it is the audit headline. Derived from `AC_CARD_REASON_EXEMPT_ACTIONS`. */
	reason_required?: boolean;
}

/**
 * The steps that need NO reason (user decision 2026-10-30, ticket "every
 * status change must have a reason except Approve and few"): submitting a
 * draft / resubmitting after a return, and approving. Every other edge needs
 * one — Return / Reject through their remark for the organisation
 * (`remarks_required`, which IS their reason), every other step through
 * `reason` (the audit headline). The SUPER_ADMIN override always needs one.
 * This list is the ONE place the rule lives; `AC_CARD_TRANSITIONS` derives
 * `reason_required` from it, and the API and the GMS read the edges.
 */
export const AC_CARD_REASON_EXEMPT_ACTIONS = ["submit", "approve"] as const satisfies readonly AcCardTransitionAction[];

/**
 * The steps the GMS takes without asking "are you sure?" (user decision
 * 2026-10-30): only Submit / Resubmit. Every other step — Approve included —
 * opens the confirmation dialog, single and bulk; a reason, when the edge
 * needs one, is typed in that same dialog.
 */
export const AC_CARD_CONFIRM_EXEMPT_ACTIONS = ["submit"] as const satisfies readonly AcCardTransitionAction[];

/** Does taking `action` ask for confirmation in the GMS? */
export const transitionNeedsConfirm = (action: AcCardTransitionAction): boolean => !(AC_CARD_CONFIRM_EXEMPT_ACTIONS as readonly string[]).includes(action);

type AcCardEdge = Omit<AcCardTransition, "reason_required">;

/**
 * Every allowed status change (PLAN §3). Creation (→ DRAFT) is not an edge.
 *
 * The same (from, to) pair may appear once per actor: a withdrawal from DRAFT,
 * SUBMITTED or RETURN is open to the organisation AND the host. "Not the person
 * who submitted it" on approve is a service rule (`SELF_APPROVAL`), not an edge.
 */
const AC_CARD_EDGES: readonly AcCardEdge[] = [
	{ from: "DRAFT", to: "SUBMITTED", by: "ORG", action: "submit" },
	{ from: "SUBMITTED", to: "DRAFT", by: "ORG", action: "unsubmit" },
	{ from: "SUBMITTED", to: "PENDING", by: "HOST", action: "pending" },
	{ from: "SUBMITTED", to: "APPROVED", by: "HOST", action: "approve" },
	{ from: "PENDING", to: "APPROVED", by: "HOST", action: "approve" },
	{ from: "SUBMITTED", to: "RETURN", by: "HOST", action: "return", remarks_required: true },
	{ from: "PENDING", to: "RETURN", by: "HOST", action: "return", remarks_required: true },
	{ from: "RETURN", to: "SUBMITTED", by: "ORG", action: "submit" },
	{ from: "SUBMITTED", to: "REJECTED", by: "HOST", action: "reject", remarks_required: true },
	{ from: "PENDING", to: "REJECTED", by: "HOST", action: "reject", remarks_required: true },
	{ from: "DRAFT", to: "WITHDRAWN", by: "ORG", action: "withdraw" },
	{ from: "DRAFT", to: "WITHDRAWN", by: "HOST", action: "withdraw" },
	{ from: "SUBMITTED", to: "WITHDRAWN", by: "ORG", action: "withdraw" },
	{ from: "SUBMITTED", to: "WITHDRAWN", by: "HOST", action: "withdraw" },
	{ from: "RETURN", to: "WITHDRAWN", by: "ORG", action: "withdraw" },
	{ from: "RETURN", to: "WITHDRAWN", by: "HOST", action: "withdraw" },
	{ from: "PENDING", to: "WITHDRAWN", by: "HOST", action: "withdraw" },
	{ from: "APPROVED", to: "WITHDRAWN", by: "HOST", action: "withdraw" },
	{ from: "REJECTED", to: "PENDING", by: "SUPER_ADMIN", action: "reopen" },
	{ from: "WITHDRAWN", to: "PENDING", by: "SUPER_ADMIN", action: "reopen" },
	{ from: "APPROVED", to: "PENDING", by: "SUPER_ADMIN", action: "reopen" },
];

/** The graph, each edge with `reason_required` from `AC_CARD_REASON_EXEMPT_ACTIONS`. */
export const AC_CARD_TRANSITIONS: readonly AcCardTransition[] = AC_CARD_EDGES.map((e) => ({
	...e,
	reason_required: !e.remarks_required && !(AC_CARD_REASON_EXEMPT_ACTIONS as readonly string[]).includes(e.action),
}));

/** The edges leaving `from`, in graph order. */
export const allowedTransitions = (from: AcCardStatus): AcCardTransition[] => AC_CARD_TRANSITIONS.filter((t) => t.from === from);

/** The edge for `action` from `from` taken by `by`, if the graph has one. */
export const findTransition = (from: AcCardStatus, action: AcCardTransitionAction, by: AcCardTransitionActor): AcCardTransition | undefined =>
	AC_CARD_TRANSITIONS.find((t) => t.from === from && t.action === action && t.by === by);

/** The distinct (from, to) pairs — what the database trigger allows, whoever asks. */
export const acCardTransitionPairs = (): Array<[AcCardStatus, AcCardStatus]> => {
	const seen = new Set<string>();
	const out: Array<[AcCardStatus, AcCardStatus]> = [];
	for (const t of AC_CARD_TRANSITIONS) {
		const key = `${t.from}>${t.to}`;
		if (!seen.has(key)) {
			seen.add(key);
			out.push([t.from, t.to]);
		}
	}
	return out;
};

// ── Limits ──────────────────────────────────────────────────────────────────

/** `POST /accreditations/bulk/*` with explicit `ids`. */
export const BULK_ACCREDITATIONS_MAX_IDS = 100;
/** `POST /accreditations/bulk/*` with a `filter` ("Select all N"): refused above this many rows. */
export const BULK_ACCREDITATIONS_MAX_FILTER = 2000;
/** `GET /accreditations/export.csv` reads the list query in keyset chunks of this size. */
export const EXPORT_CHUNK = 1000;
/** `status_note` (the RETURN / REJECT remark) and other card remarks. */
export const AC_CARD_REMARKS_MAX = 1000;
/**
 * `POST /accreditations/check-id`: at most this many checks per login per
 * window, then 429 RATE_LIMITED. The check answers free / registered for any
 * ID number, so unlimited it is an ID-probing oracle. ADMIN / SUPER_ADMIN are
 * exempt (they see the owners anyway).
 */
export const ACCREDITATION_ID_CHECK_MAX = 60;
export const ACCREDITATION_ID_CHECK_WINDOW_SECONDS = 600;
/** Records list page sizes offered by the UI; the API accepts 1..PAGE_SIZE_MAX. */
export const ACCREDITATION_PAGE_SIZES = [20, 50, 100] as const;
export const ACCREDITATION_PAGE_SIZE_DEFAULT = 20;
/** `registration_no` is six digits: CHECK 1..999999 in the database. */
export const REGISTRATION_NO_MAX = 999_999;

// ── Numbers: one helper prints them everywhere ──────────────────────────────

const pad6 = (n: number) => String(n).padStart(6, "0");

/** The middle part of a card number when the organisation has no country code (not a NOC). */
export const REGISTRATION_NO_FALLBACK_ORG = "GMS";
/** The leading part of a registration number in the GMS: `REG-THA-000123`. */
export const REGISTRATION_NO_PREFIX = "REG";
/** The leading part of an accreditation number in the GMS: `ACC-THA-000123`. */
export const ACCREDITATION_NO_PREFIX = "ACC";

const orgPart = (orgCode: string | null) => orgCode?.trim() || REGISTRATION_NO_FALLBACK_ORG;

/**
 * `REG-THA-000123`: the registration number, which every card has from
 * creation, as the GMS shows it. `orgCode` is the responsible organisation's
 * country IOC code for a NOC-type organisation, otherwise null → `GMS`.
 * No games prefix (decided by the user 2026-10-15).
 */
export const formatRegistrationNo = (orgCode: string | null, n: number): string => `${REGISTRATION_NO_PREFIX}-${orgPart(orgCode)}-${pad6(n)}`;

/** `ACC-THA-000123`: the accreditation number (exists once the card is approved; it equals `registration_no`), as the GMS shows it. */
export const formatAccreditationNo = (orgCode: string | null, n: number): string => `${ACCREDITATION_NO_PREFIX}-${orgPart(orgCode)}-${pad6(n)}`;

/** `000123`: the number alone, as printed on the physical accreditation card face. */
export const formatCardNumber = (n: number): string => pad6(n);

/**
 * Reads a card number out of a search box: `REG-THA-000123`,
 * `ACC-GMS-000123`, `123`, `000123` (the printed card face), and the older
 * forms `AC-000123`, `SG27-THA-000123`, `THA-123` (still accepted) → 123.
 * Anything else → null (the search then tries an ID number, then a name). Prefix segments must start with a letter,
 * so a plain ID number like `900101-14-5678` is not mistaken for a card number.
 */
export function parseCardNumberQuery(q: string): number | null {
	const m = /^(?:[A-Za-z][A-Za-z0-9]{0,9}-){0,2}(\d{1,6})$/.exec(q.trim());
	if (!m) return null;
	const n = Number(m[1]);
	return n >= 1 && n <= REGISTRATION_NO_MAX ? n : null;
}

/** The fewest digits a partial card-number search matches on (a trigram: fewer would match a large share of every card). */
export const CARD_NUMBER_FRAGMENT_MIN = 3;

/**
 * A bare run of 3–5 digits in a search box is also a PART of a card number
 * (user, 2026-11-05): `181` and `129` both find `018129`, matched against the
 * printed six digits (`formatCardNumber`), leading zeros included. Six digits
 * are the whole number already; prefixed forms (`REG-THA-181`) stay exact.
 */
export function cardNumberFragment(q: string): string | null {
	const v = q.trim();
	return /^\d+$/.test(v) && v.length >= CARD_NUMBER_FRAGMENT_MIN && v.length < 6 ? v : null;
}

// ── Records list query ──────────────────────────────────────────────────────

export const ACCREDITATION_SORTS = ["number_desc", "number_asc", "updated_desc", "name_asc"] as const;
export type AccreditationSort = (typeof ACCREDITATION_SORTS)[number];

const splitList = (v: string) =>
	v
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);

/** `status=SUBMITTED,PENDING` — one or more, comma-separated, de-duplicated. */
const statusList = z
	.string()
	.max(200)
	.transform(splitList)
	.pipe(z.array(z.enum(AC_CARD_STATUSES)).min(1))
	.transform((list) => [...new Set(list)]);

/**
 * A yes/no query parameter: exactly `true` or `false`. Never
 * `z.coerce.boolean()` — `Boolean("false")` is true (CLAUDE.md, bug 2).
 */
const booleanQueryParam = z.enum(["true", "false"]).transform((v) => v === "true");

/**
 * `travel=` on the records list (M9): cards with / without an arrival or a
 * departure leg — the A&D "approved without arrival" tile links here
 * (`status=APPROVED&travel=missing_arrival`). An anti-join on the
 * `travel_legs (ac_card_id, direction)` unique index.
 */
export const ACCREDITATION_TRAVEL_FILTERS = ["missing_arrival", "has_arrival", "missing_departure", "has_departure"] as const;
export type AccreditationTravelFilter = (typeof ACCREDITATION_TRAVEL_FILTERS)[number];

/**
 * `GET /accreditations` (and `/summary`, `/export.csv`, which ignore paging).
 * Wire keys, snake_case; the API maps them to camelCase.
 *
 * Keyset paging only (Rule 0): `cursor` is opaque — send back `next_cursor`
 * exactly as received. The total comes with the first page (no cursor) only.
 * `q` matches a card number (`parseCardNumberQuery`), else an ID number, else
 * a name.
 */
export const accreditationListQuery = keysetQuery.extend({
	games_id: z.string().uuid().optional(),
	status: statusList.optional(),
	responsible_organization_id: z.string().uuid().optional(),
	main_function_id: z.string().uuid().optional(),
	sub_agency_id: z.string().uuid().optional(),
	sub_function_id: z.string().uuid().optional(),
	urgent: booleanQueryParam.optional(),
	/** Only people holding more than one card. */
	multicard: booleanQueryParam.optional(),
	/**
	 * Only cards with no photo that still stand (not REJECTED / WITHDRAWN): the
	 * "No photo" chip — such a card cannot print (M4).
	 */
	no_photo: booleanQueryParam.optional(),
	/** M9. Cards with / without a travel leg. */
	travel: z.enum(ACCREDITATION_TRAVEL_FILTERS).optional(),
	q: searchParam.optional(),
	sort: z.enum(ACCREDITATION_SORTS).default("number_desc"),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(ACCREDITATION_PAGE_SIZE_DEFAULT),
});
export type AccreditationListQuery = z.infer<typeof accreditationListQuery>;
export type AccreditationListQueryInput = z.input<typeof accreditationListQuery>;

// ── Registration windows ────────────────────────────────────────────────────

/**
 * A window during which scoped logins may register cards of one category for
 * one games (optionally one organisation; null = every organisation). No row =
 * closed for scoped roles; admins are never window-bound.
 */
export const registrationWindowFields = z
	.object({
		games_id: z.string().uuid(),
		main_function_id: z.string().uuid(),
		responsible_organization_id: z.string().uuid().nullable(),
		opens_at: z.string().datetime({ offset: true }),
		closes_at: z.string().datetime({ offset: true }),
	})
	.refine((v) => Date.parse(v.closes_at) > Date.parse(v.opens_at), { path: ["closes_at"], message: "Closes must be after opens" });
export type RegistrationWindowFields = z.infer<typeof registrationWindowFields>;
